import React, { useEffect, useState } from "react";
import GradientButton from "../../components/button/Button";
import PlainButton from "../../components/button/PlainButton";
import { Cam } from "../../images/Images";
import { ToggleOnIcon } from "../../components/icons/Icons";
import axiosInstance from "../../axiosInstance";
import { toast } from "react-toastify";
import LoadingComponent from "../../components/loading/Loading";
import useAuth from "../../state/useAuth";

interface FilterModalProps {
  isOpen: boolean;
  activeStock: any;
  onClose: () => void;
}

interface ProductSegment {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  name: string;
  description: string;
  image: string;
}

interface Venture {
  name: string;
  id: string;
}

const EditStockModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,

  activeStock,
}) => {
  const { id } = useAuth();

  const [filters, setFilters] = useState({
    name: "",
    category: "",
    productDesc: "",
    amount: "",
    stockQuantity: "",
    vendor: id,
    tags: "",
    threshold: "",
    image: null as File | null,
  });

  const [segments, setSegments] = useState<ProductSegment[]>([]);
  const [ventures, setVentures] = useState<Venture[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploadPercent, setUploadPercent] = useState<number | undefined>(
    undefined
  );
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [thresholdError, setThresholdError] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));

    if (name === "threshold") {
      setThresholdError(Number(value) < 50);
    }
  };

  const getCategories = async () => {
    setLoadingCategories(true);
    try {
      const res = await axiosInstance.get("/segments");
      if (res?.data) {
        setSegments(res.data?.data || []);
      }
      setLoadingCategories(false);
    } catch (error) {
      setLoadingCategories(false);
      console.error("Error fetching categories:", error);
    }
  };

  const getVentures = async () => {
    try {
      const res = await axiosInstance.get("/ventures");
      if (res?.data) {
        setVentures(res.data?.data || []);
      }
    } catch (error) {
      console.error("Error fetching vendors:", error);
    }
  };

  useEffect(() => {
    getCategories();
    getVentures();
  }, []);

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setFilters((prevFilters) => ({
        ...prevFilters,
        image: file,
      }));

      // Create an image preview URL
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  // Populate fields with activeStock data
  useEffect(() => {
    if (activeStock) {
      setFilters({
        name: activeStock.name || "",
        threshold: activeStock.threshold || "",
        category: activeStock.segment?.id || "", // Set to the category id
        productDesc: activeStock.description || "",
        amount: activeStock.amount || "",
        stockQuantity: activeStock.stockQuantity || "",
        vendor: activeStock.vendor || "",
        tags: activeStock.tags || "",
        image: null, // Reset image since it cannot be pre-filled
      });
      setImagePreview(activeStock.image || null); // Set image preview
    }
  }, [activeStock]);

  const handleSubmit = async () => {
    // Prevent submit if threshold is less than 50
    if (Number(filters.threshold) < 50) {
      setThresholdError(true);
      toast.error("Minimum threshold must not be less than 50.");
      return;
    }

    setLoading(true);

    const formData = new FormData();
    formData.append("name", filters.name);
    formData.append("description", filters.productDesc);
    formData.append("amount", filters.amount);
    formData.append("unit", "per kg");
    formData.append("tags", filters.tags);
    if (id) {
      formData.append("vendor", id);
    }
    formData.append("segment_id", filters.category);

    if (filters.image instanceof File) {
      formData.append("image", filters.image);
    }

    try {
      const res = await axiosInstance.put(
        `/products/${activeStock.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              setUploadPercent(percentCompleted);
            }
          },
        }
      );

      console.log("Response:", res.data);

      if (res.data) {
        toast.success("Product updated successfully!");
        setLoading(false);
        setUploadPercent(undefined);
        onClose();
        window.location.reload();
      }
    } catch (error: any) {
      console.error("Error:", error);

      if (error.response?.data?.errors) {
        const fieldErrors = error.response.data.errors;

        toast.error(error.response.data.message || "Something went wrong");

        for (const field in fieldErrors) {
          toast.error(`${field}: ${fieldErrors[field]}`);
        }
      } else {
        toast.error(error.response?.data?.message || "Something went wrong");
      }

      setLoading(false);
      setUploadPercent(undefined);
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return <LoadingComponent percent={uploadPercent} />;
  }

  return (
    <div className="orders-modal">
      <div className="orders-modal__overlay" onClick={onClose}></div>
      <div
        className="orders-modal__content"
        style={{ height: "570px", overflowY: "scroll" }}
      >
        <div className="orders__modal__header">
          <h2 className="orders-modal__title">Edit Stock</h2>
          <button className="orders-modal__close" onClick={onClose}>
            &times;
          </button>
        </div>
        <form
          className="orders-modal__form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {/* Product Name */}
          <div className="orders-modal__form-group">
            <label htmlFor="name">Product Name</label>
            <input
              type="text"
              id="name"
              name="name"
              placeholder="Enter product name"
              value={filters.name}
              onChange={handleChange}
            />
          </div>

          {/* Category */}
          <div className="orders-modal__form-group">
            <label htmlFor="category">Category</label>
            <select
              id="category"
              name="category"
              value={filters.category}
              onChange={handleChange}
            >
              <option value="">Select category</option>
              {loadingCategories ? (
                <option disabled>Loading categories...</option>
              ) : (
                segments.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* threshold */}
          <div className="orders-modal__form-group">
            <label htmlFor="threshold">Minimum Threshold</label>
            <input
              type="number"
              id="threshold"
              name="threshold"
              value={filters.threshold}
              onChange={handleChange}
              placeholder="E.g. 150"
              style={thresholdError ? { borderColor: "#f44336" } : {}}
            />
            {thresholdError && (
              <div style={{ color: "#f44336", fontSize: 13, marginTop: 2 }}>
                Value must not be less than 50
              </div>
            )}
          </div>

          {/* Product Amount */}
          <div className="orders-modal__form-group">
            <label htmlFor="amount">Product Amount</label>
            <input
              type="text"
              id="amount"
              name="amount"
              value={filters.amount}
              onChange={handleChange}
              placeholder="E.g. $150"
            />
          </div>

          {/* Tags */}
          <div className="orders-modal__form-group">
            <label htmlFor="tags">Product Tags</label>
            <input
              type="text"
              id="tags"
              name="tags"
              placeholder="E.g flakes,cereal"
              value={filters.tags}
              onChange={handleChange}
            />
          </div>

          {/* Product Description */}
          <div className="orders-modal__form-group" style={{ width: "100%" }}>
            <label htmlFor="productDesc">Product Description</label>
            <textarea
              id="productDesc"
              name="productDesc"
              placeholder="E.g. Yam Flour is made from Yam"
              value={filters.productDesc}
              onChange={handleChange}
              style={{
                width: "100%",
                padding: 4,
                resize: "none",
                outline: "none",
                borderRadius: 5,
                border: "1px solid #ccc",
                height: 100,
              }}
            />
          </div>

          {/* Image Upload */}
          <div
            className="orders-modal__form-group form__upload"
            style={{ width: "100%" }}
          >
            <input
              type="file"
              accept="image/png, image/jpeg"
              style={{ display: "none" }}
              id="imageUpload"
              onChange={handleImageChange}
            />
            <label
              htmlFor="imageUpload"
              style={{ textAlign: "center", cursor: "pointer" }}
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{ width: 50, height: 50, borderRadius: 5 }}
                />
              ) : (
                <>
                  <img
                    src={Cam}
                    alt="Upload"
                    style={{ width: 25, height: 25 }}
                  />
                  <p>Upload Image</p>
                  <p style={{ color: "#7E7E7E" }}>JPG and PNG, less than 1MB</p>
                </>
              )}
            </label>
          </div>

          {/* Actions */}
          <div className="orders-modal__actions stock">
            <PlainButton onClick={handleSubmit} text="Submit" />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditStockModal;
